import { useEffect, useRef, useState } from "react";

/**
 * Shared auto-fitting text used by BOTH the private Canvas editor and the public
 * Grid publication layer, so a Territory's text renders PIXEL-IDENTICALLY in
 * both surfaces. Binary-searches the largest font (≤ box height) at which the
 * CURRENT text fits the box on both axes; refits on box-resize/zoom, never on
 * text length changing size erratically (the box is the single source of truth).
 */
export function AutoFitText({
  text, maxFontPx, font, color, bold, italic, underline, align, lineHeight,
}: {
  text: string; maxFontPx: number; font: string; color: string; bold: boolean; italic: boolean;
  underline: boolean; align: string; lineHeight: number;
}) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [fontPx, setFontPx] = useState(maxFontPx);
  useEffect(() => {
    const outer = outerRef.current, inner = innerRef.current;
    if (!outer || !inner) return;
    let raf = 0;
    const measure = () => {
      const availW = outer.clientWidth, availH = outer.clientHeight;
      if (availW <= 0 || availH <= 0) return;
      let lo = 1, hi = Math.max(1, availH), best = 1;
      for (let i = 0; i < 16; i++) {
        const mid = (lo + hi) / 2;
        inner.style.fontSize = `${mid}px`;
        const fits = inner.scrollWidth <= availW + 0.5 && inner.scrollHeight <= availH + 0.5;
        if (fits) { best = mid; lo = mid; } else { hi = mid; }
      }
      inner.style.fontSize = `${best}px`;
      setFontPx(best);
    };
    raf = requestAnimationFrame(measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      ro.observe(outer);
    }
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); };
  }, [text, maxFontPx, font, bold, italic, lineHeight, align]);
  const justify = align === "CENTER" ? "center" : align === "RIGHT" ? "flex-end" : "flex-start";
  const textAlign = align === "CENTER" ? "center" : align === "RIGHT" ? "right" : align === "JUSTIFY" ? "justify" : "left";
  return (
    <div ref={outerRef} style={{ width: "100%", height: "100%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: justify }}>
      <div ref={innerRef} style={{ maxWidth: "100%", maxHeight: "100%", fontFamily: font, fontSize: fontPx, color,
        fontWeight: bold ? 700 : 400, fontStyle: italic ? "italic" : "normal", textDecoration: underline ? "underline" : "none",
        textAlign: textAlign as "left" | "center" | "right" | "justify", whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight }}>
        {text}
      </div>
    </div>
  );
}
